<template>
  <div class="task-choice-container">
    <div class="task-choice-content">
      <!-- Current User Display -->
      <div v-if="user" class="current-user-card">
        <v-avatar color="primary" size="48">
          <span class="white--text text-h6">{{ userInitials }}</span>
        </v-avatar>
        <div class="user-info">
          <div class="user-name">{{ user.name || user.user }}</div>
          <div class="user-role">{{ userRoleLabel }}</div>
        </div>
      </div>

      <!-- Question -->
      <h1 class="task-question">Was möchtest du tun?</h1>

      <!-- Task Buttons -->
      <div class="task-buttons">
        <!-- Artikel einlagern -->
        <div class="task-card primary" @click="goToMulticartPlace">
          <div class="task-card-header">
            <v-icon color="white" size="32">mdi-warehouse</v-icon>
          </div>
          <div class="task-card-content">
            <div class="task-card-title">Artikel einlagern</div>
            <div class="task-card-subtitle">Artikel ins Regal einlagern</div>
          </div>
        </div>

        <!-- Bestellungen packen (Lower Shelf) -->
        <div class="task-card secondary" @click="goToMulticartWizard('lower')">
          <div class="task-card-header">
            <v-icon color="white" size="32">mdi-package-variant</v-icon>
          </div>
          <div class="task-card-content">
            <div class="task-card-title">Bestellungen packen</div>
            <div class="task-card-subtitle">Artikel für den Versand vorbereiten</div>
          </div>
          <div v-if="multicartBadgeCountLower > 0" class="task-card-badge">
            {{ multicartBadgeCountLower }}
          </div>
        </div>

        <!-- Bestellungen oben packen (Upper Shelf) -->
        <div class="task-card secondary" @click="goToMulticartWizard('upper')">
          <div class="task-card-header">
            <v-icon color="white" size="32">mdi-package-variant</v-icon>
          </div>
          <div class="task-card-content">
            <div class="task-card-title">Bestellungen oben packen</div>
            <div class="task-card-subtitle">Artikel für den Versand oben vorbereiten</div>
          </div>
          <div v-if="multicartBadgeCountUpper > 0" class="task-card-badge">
            {{ multicartBadgeCountUpper }}
          </div>
        </div>

        <!-- Retoure einbuchen - DISABLED -->
        <!-- <div class="task-card retoure" @click="goToRetoureShelf">
          <div class="task-card-header">
            <v-icon color="white" size="32">mdi-package-variant-closed</v-icon>
          </div>
          <div class="task-card-content">
            <div class="task-card-title">Retoure einbuchen</div>
            <div class="task-card-subtitle">Rücksendung ins Regal einbuchen</div>
          </div>
        </div> -->

        <!-- Admin: Key Assignment -->
        <div v-if="isAdmin" class="task-card warning" @click="goToKeyAssignment">
          <div class="task-card-header">
            <v-icon color="white" size="32">mdi-key</v-icon>
          </div>
          <div class="task-card-content">
            <div class="task-card-title">Schlüssel zuweisen</div>
            <div class="task-card-subtitle">Schlüssel einem Benutzer zuweisen</div>
          </div>
        </div>

        <!-- Logout -->
        <div class="task-card error" @click="logout">
          <div class="task-card-header">
            <v-icon color="white" size="32">mdi-logout</v-icon>
          </div>
          <div class="task-card-content">
            <div class="task-card-title">Abmelden</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService';
import PacklistService from '@/services/PacklistService';

export default {
  name: 'WarehouseTaskChoice',
  data() {
    return {
      user: null,
      multicartBadgeCount: 0,
      multicartBadgeCountLower: 0,
      multicartBadgeCountUpper: 0
    };
  },
  computed: {
    isAdmin() {
      return this.user?.role === 'admin';
    },
    userName() {
      if (!this.user) return '';
      const name = this.user.name || this.user.user || '';
      return name ? `, ${name}` : '';
    },
    userInitials() {
      if (!this.user) return '?';
      const name = this.user.name || this.user.user || '';
      if (!name) return '?';

      // Get first letter of first and last name
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      // If only one name, use first two letters
      return name.substring(0, 2).toUpperCase();
    },
    userRoleLabel() {
      if (!this.user) return '';

      const roleMap = {
        'admin': 'Administrator',
        'packer': 'Packer',
        'production': 'Produktion',
        'support': 'Support'
      };

      return roleMap[this.user.role] || this.user.role || 'Benutzer';
    }
  },
  async mounted() {
    try {
      const response = await AuthService.getUser();
      this.user = response.data;
      console.log('User loaded:', this.user);
    } catch (error) {
      console.error('Error loading user:', error);
    }

    // Load multicart badge count
    await this.loadMulticartBadgeCount();
  },
  methods: {
    goToMulticartPlace() {
      this.$router.push('/warehouse/place');
    },
    goToMulticartWizard(shelf = 'lower') {
      this.$router.push(`/multicart-wizard?shelf=${shelf}`);
    },
    goToRetoureShelf() {
      this.$router.push('/warehouse/retoure');
    },
    goToKeyAssignment() {
      this.$router.push('/admin/key-assignment');
    },
    logout() {
      this.$router.push('/auth/logout');
    },
    async loadMulticartBadgeCount() {
      try {
        // Load both lower and upper shelf counts in parallel
        const [lowerData, upperData] = await Promise.all([
          PacklistService.getMultiCartOrders('lower'),
          PacklistService.getMultiCartOrders('upper')
        ]);

        this.multicartBadgeCountLower = lowerData.data.length;
        this.multicartBadgeCountUpper = upperData.data.length;
        this.multicartBadgeCount = this.multicartBadgeCountLower + this.multicartBadgeCountUpper;

        console.log('Multicart badge counts - Lower:', this.multicartBadgeCountLower, 'Upper:', this.multicartBadgeCountUpper);
      } catch (error) {
        console.error('Error loading multicart badge count:', error);
        this.multicartBadgeCount = 0;
        this.multicartBadgeCountLower = 0;
        this.multicartBadgeCountUpper = 0;
      }
    }
  }
};
</script>

<style scoped>
.task-choice-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #ffffff;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 40px 20px;
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.task-choice-content {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding-bottom: 40px;
}

/* Current User Card */
.current-user-card {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.user-role {
  font-size: 14px;
  color: #666;
  line-height: 1.2;
}

/* Question */
.task-question {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  text-align: center;
  margin: 0;
  line-height: 1.3;
  padding: 0 10px;
}

/* Task Buttons */
.task-buttons {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.task-card:active {
  transform: translateY(0);
}

.task-card.primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.task-card.secondary {
  background: linear-gradient(135deg, #43a047 0%, #388e3c 100%);
}

.task-card.retoure {
  background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%);
}

.task-card.warning {
  background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
}

.task-card.error {
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
}

.task-card-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.task-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: white;
}

.task-card-title {
  font-size: clamp(16px, 4vw, 20px);
  font-weight: bold;
  line-height: 1.2;
}

.task-card-subtitle {
  font-size: clamp(12px, 3vw, 14px);
  opacity: 0.9;
  line-height: 1.3;
}

/* Badge */
.task-card-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #f44336;
  color: white;
  font-size: 14px;
  font-weight: bold;
  min-width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

/* Responsive Adjustments */
@media (max-width: 480px) {
  .task-choice-container {
    padding: 20px 16px 30px 16px;
    align-items: flex-start;
  }

  .task-choice-content {
    gap: 20px;
    padding-bottom: 30px;
  }

  .current-user-card {
    padding: 12px;
    gap: 12px;
  }

  .user-name {
    font-size: 16px;
  }

  .user-role {
    font-size: 13px;
  }

  .task-question {
    font-size: 24px;
    padding: 0 8px;
  }

  .task-buttons {
    gap: 10px;
  }

  .task-card {
    padding: 14px;
    gap: 12px;
  }

  .task-card-badge {
    font-size: 12px;
    min-width: 24px;
    height: 24px;
    top: -6px;
    right: -6px;
  }
}

@media (max-width: 380px) {
  .task-choice-container {
    padding: 16px 12px 24px 12px;
  }

  .current-user-card {
    padding: 10px;
    gap: 10px;
  }

  .user-name {
    font-size: 15px;
  }

  .user-role {
    font-size: 12px;
  }

  .task-question {
    font-size: 22px;
  }

  .task-buttons {
    gap: 8px;
  }

  .task-card {
    padding: 12px;
    gap: 10px;
  }
}
</style>
